import axios from 'axios'
import { API_URL } from './config'

/* A buyer's sign-in, and the only thing on the storefront that knows where
   the token is kept.

   Deliberately not React state. The axios instance in ./http needs the token
   on every request and runs outside the component tree, so the token lives
   here, in one module, and the React layer subscribes to it.

   The key is not the console's. Both apps run on localhost in development
   and could otherwise share a browser origin — a customer signing in here
   would overwrite the console's session in the next tab, and the console
   would find itself holding a customer's token. One key each keeps the two
   doors separate. */

const KEY = 'sm28.storefront.session'

export type AccountUser = {
  id: string
  username: string
  fullName: string
  email: string
  phone: string
  roleId: string
  roleName: string
  scope: 'staff' | 'customer'
  permissions: string[]
  /** The customer record this sign-in belongs to. Everything on the account
      pages is read through it. */
  customerId: string
  active: boolean
  lastLoginAt: string
  createdAt: string
  updatedAt: string
}

export type Session = {
  token: string
  /** Milliseconds since the epoch, from the API. */
  expiresAt: number
  user: AccountUser
}

const listeners = new Set<(session: Session | null) => void>()

let current: Session | null = load()

function load(): Session | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Session
    if (!parsed?.token || typeof parsed.expiresAt !== 'number') return null
    if (!parsed.user || !Array.isArray(parsed.user.permissions)) return null

    /* An expired token is discarded here rather than sent: the API would
       refuse it, and the page would show a failed request before it showed
       the sign-in form. */
    if (parsed.expiresAt <= Date.now()) return null

    return parsed
  } catch {
    /* Storage disabled, or a value written by another version. Not signed in
       either way. */
    return null
  }
}

export const getSession = () => current
export const getToken = () => current?.token ?? ''

/** Does this buyer's role allow that? There is one role on the storefront
    and it holds two rights, so this is thin — but it is the same question
    the API asks, and reading the answer rather than assuming it is what
    stops the account pages from being drawn for somebody whose access has
    been narrowed. */
export const holds = (permission: string) =>
  current?.user.permissions.includes(permission) ?? false

function set(session: Session | null) {
  current = session

  try {
    if (session) localStorage.setItem(KEY, JSON.stringify(session))
    else localStorage.removeItem(KEY)
  } catch {
    /* Private browsing with storage blocked. The session works for this tab
       from the value held above; it just will not survive a reload. */
  }

  for (const listener of listeners) listener(session)
}

export function clearSession() {
  if (current) set(null)
}

export function onSessionChange(listener: (session: Session | null) => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/* Sign-in and sign-up go out on a bare instance rather than the shared one:
   the shared instance clears the session on a 401, and a mistyped password
   would then read as being signed out rather than as a wrong password. */
const plain = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 20_000,
  headers: { Accept: 'application/json' },
})

type AuthResponse = { token: string; expiresAt: number; user: AccountUser }

/** Staff sign in to the console, not here. Their password is right, so this
    is not a failed sign-in — it is the wrong door, and saying so is better
    than an account page with nothing on it. */
export class StaffAccountError extends Error {
  constructor() {
    super('That sign-in belongs to the showroom console.')
    this.name = 'StaffAccountError'
  }
}

function accept(data: AuthResponse) {
  if (data.user.scope !== 'customer') throw new StaffAccountError()

  const session: Session = {
    token: data.token,
    expiresAt: data.expiresAt,
    user: data.user,
  }

  set(session)
  return session
}

export async function signIn(username: string, password: string) {
  const { data } = await plain.post<AuthResponse>('/auth/login', { username, password })
  return accept(data)
}

export type RegisterInput = {
  fullName: string
  username: string
  phone: string
  email?: string
  password: string
}

/** Registering signs you straight in — a buyer who has just typed their name,
    number and a password should not be asked for two of them again. */
export async function register(input: RegisterInput) {
  const { data } = await plain.post<AuthResponse>('/auth/register', input)
  return accept(data)
}

/** Confirms the stored token with the API, and takes the role back from it.

    Called once on load. A token can be refused long before it expires — the
    server's secret changed, or the account was suspended — and finding that
    out at start-up is better than finding it out halfway through a page. */
export async function verifySession() {
  if (!current) return null

  try {
    const { data } = await plain.get<{ user: AccountUser }>('/auth/me', {
      headers: { Authorization: `Bearer ${current.token}` },
    })

    if (data.user.scope !== 'customer') {
      clearSession()
      return null
    }

    const refreshed: Session = { ...current, user: data.user }
    if (JSON.stringify(refreshed.user) !== JSON.stringify(current.user)) set(refreshed)

    return refreshed
  } catch (error) {
    /* Only a refusal means signed out. An unreachable API is a network
       problem, and sending somebody to a sign-in form they cannot use either
       would be the wrong reading of it. */
    if (axios.isAxiosError(error) && error.response) {
      clearSession()
      return null
    }
    return current
  }
}

export function signOut() {
  const token = current?.token
  clearSession()

  /* Nothing on the server to revoke — the token is checked by its signature
     — so this is a courtesy the API may ignore. Failing it must not undo the
     sign-out that has already happened here. */
  if (token) {
    plain
      .post('/auth/logout', null, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {})
  }
}
